import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, X, Sparkles, Copy, Clock, RotateCcw, CheckCircle2, CircleDashed } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PromptLangPicker from "@/components/PromptLangPicker";

import { countryNames, countryName, flattenRegions } from "@/lib/countries";
import SpellCheck from "@/components/SpellCheck";
import PublicFooter from "@/components/PublicFooter";
import CompareMarkets from "@/components/CompareMarkets";
import CurrencySelect from "@/components/CurrencySelect";
import { useCurrency } from "@/context/CurrencyContext";

const DRAFT_KEY = "kadana_prompt_draft_v1"; // SW1 — auto-saved landing prompt draft

const JURISDICTIONS = ["UK", "EU Single Market", "Pan-European", "Global",
  "DE", "FR", "IT", "ES", "PL", "NL", "BE", "RO", "GR", "PT", "SE", "NO", "FI", "DK", "EE", "IE"];

function ConnectorPreview({ onClose }) {
  const [items, setItems] = useState([]); const [total, setTotal] = useState(0); const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0); const [hasMore, setHasMore] = useState(false);
  const LIMIT = 40;
  const load = async (reset = true) => {
    try {
      const off = reset ? 0 : offset;
      const { data } = await api.get("/public/catalogue-preview", { params: { q, limit: LIMIT, offset: off } });
      setItems(reset ? (data.items || []) : [...items, ...(data.items || [])]);
      setTotal(data.total_connectors || 0); setHasMore(!!data.has_more); setOffset(off + (data.items || []).length);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, []);
  const badge = { live: "k-tag-green", sandbox: "k-tag-warn" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(17,17,19,.6)", zIndex: 60 }} onClick={onClose}>
      <div className="k-container k-fadein" data-testid="connector-preview" onClick={e => e.stopPropagation()}
        style={{ position: "absolute", right: 0, top: 0, height: "100vh", width: "min(480px, 100vw)", borderRadius: 0, padding: 24, overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 className="k-heading" style={{ fontSize: 20, margin: 0 }}>Connector catalogue</h2>
          <button className="k-btn k-btn-ghost" data-testid="connector-close" onClick={onClose}><X size={16} /></button>
        </div>
        <p className="k-muted" style={{ fontSize: 13 }}>
          Browse {total} real APIs across UK and EU government, finance, tax, health and more.
        </p>
        <div style={{ display: "flex", gap: 8, margin: "10px 0 16px" }}>
          <input className="k-control" data-testid="connector-search" placeholder="Search connectors…"
            value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load(true)} />
          <button className="k-btn k-btn-primary" onClick={() => load(true)}>Search</button>
        </div>
        {items.map((it) => (
          <div key={it.id} className="k-control" style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <span className="k-heading" style={{ fontSize: 14, fontWeight: 600 }}>{it.name}</span>
              <span className={badge[it.status] || "k-muted"} style={{ fontSize: 12, fontWeight: 600 }}>{it.status}</span>
            </div>
            <div className="k-muted" style={{ fontSize: 12, marginTop: 4 }}>
              {countryNames(it.jurisdiction || [])} · {it.sector || "—"} · class {it.access_class}
            </div>
            <div className="k-muted" data-testid={`conn-verified-${it.id}`} style={{ fontSize: 11, marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}
              title="When an admin last confirmed this connector's access class.">
              {it.access_route_verified
                ? <><CheckCircle2 size={11} /> Access class verified by an admin</>
                : <><CircleDashed size={11} /> Access class not yet verified</>}
            </div>
          </div>
        ))}
        <div className="k-muted" style={{ fontSize: 12, textAlign: "center", margin: "6px 0" }} data-testid="connector-count">
          Showing {items.length} of {total}
        </div>
        {hasMore && (
          <button className="k-btn k-btn-ghost" data-testid="connector-load-more" style={{ width: "100%" }}
            onClick={() => load(false)}>Load more</button>
        )}
      </div>
    </div>
  );
}

export default function Landing() {
  const [m, setM] = useState(null);
  const [domains, setDomains] = useState([]);
  const [regionGroups, setRegionGroups] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [savedSel, setSavedSel] = useState(null);
  const [showResume, setShowResume] = useState(false);
  const [sector, setSector] = useState("");
  const [jur, setJur] = useState("GB");
  const [reco, setReco] = useState(null);
  const [showConnectors, setShowConnectors] = useState(false);
  const [humanCheck, setHumanCheck] = useState(false);
  const [preview, setPreview] = useState(null);
  const nav = useNavigate();
  const { format: curFormat } = useCurrency();

  useEffect(() => {
    api.get("/marketing").then(r => setM(r.data)).catch(() => {});
    api.get("/public/sector-domains").then(r => setDomains(r.data.domains || [])).catch(() => {});
    api.get("/public/region-taxonomy").then(r => setRegionGroups(flattenRegions(r.data.taxonomy))).catch(() => {});
  }, []);

  // SW1 + SW7 — auto-save the idea AND the sector/region picks; offer to resume them on return.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) { const s = JSON.parse(raw); if (s && (s.prompt || s.sector || (s.jur && s.jur !== "GB"))) { setSavedSel(s); setShowResume(true); } }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    try {
      if (prompt.trim() || sector || (jur && jur !== "GB"))
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ prompt, sector, jur }));
    } catch { /* ignore */ }
  }, [prompt, sector, jur]);

  useEffect(() => {
    if (!sector || !jur) { setReco(null); return; }
    api.get("/public/instant-preview", { params: { domain: sector, jur, idea: prompt } })
      .then(r => setReco(r.data)).catch(() => setReco(null));
  }, [sector, jur]);

  const startBuilding = () => nav("/register", { state: { prompt, sector, jurisdiction: jur, price_range: reco?.price_range } });

  // SW2 — copy a plain-text build brief; SW3 — static prompt-length guidance; SW4 — BeCo inputs.
  const sectorLabel = domains.find(d => d.domain === sector)?.label || sector;
  const becoActive = !!(prompt.trim() || sector);
  const becoClarity = becoActive ? Math.min(100, Math.round(prompt.trim().length / 3) + (sector ? 20 : 0)) : null;
  const becoComplexity = becoActive ? (reco?.layer_count != null ? Math.min(100, reco.layer_count * 12) : (sector ? 40 : 20)) : null;
  const copyBrief = async () => {
    const brief =
      "Kadana build brief\n\n" +
      `Idea: ${prompt.trim() || "(not written yet)"}\n` +
      `Sector: ${sectorLabel || "(not chosen)"}\n` +
      `Target market: ${jur ? countryName(jur) : "(not chosen)"}\n\n` +
      "Compliance-ready software — not legal advice or certification.";
    try { await navigator.clipboard.writeText(brief); toast.success("Build brief copied to your clipboard"); }
    catch { toast.error("Couldn't copy automatically — select the text and copy manually."); }
  };
  const restoreDraft = () => {
    if (savedSel) { setPrompt(savedSel.prompt || ""); if (savedSel.sector) setSector(savedSel.sector); if (savedSel.jur) setJur(savedSel.jur); }
    setShowResume(false);
  };
  const clearDraft = () => { setShowResume(false); try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  const tryPreview = async () => {
    try {
      const { data } = await api.post("/public/clarify-preview", { prompt, human_check: humanCheck });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Try again shortly."); }
  };

  return (
    <div className="k-canvas">
      <PublicNav />

      <section className="k-fadein k-glow-host" style={{ margin: "0 auto", padding: "72px 24px 24px" }}>
        <div className="k-glow k-glow--a" style={{ width: 520, height: 520, top: -140, left: "8%" }} />
        <div className="k-glow k-glow--b" style={{ width: 420, height: 420, top: -60, right: "6%" }} />
        <h1 data-testid="hero-headline" className="k-heading"
          style={{ fontSize: "clamp(44px,8.5vw,104px)", lineHeight: 1.06, margin: 0, letterSpacing: "-.03em", fontWeight: 800 }}>
          <span style={{ display: "block", textAlign: "center" }}>Think Global.</span>
          <span style={{ display: "block", textAlign: "center" }}>App Local.</span>
        </h1>
        <h2 className="k-muted" style={{ fontSize: "clamp(15px,2.1vw,28px)", letterSpacing: "-.02em",
          fontWeight: 500, marginTop: "96px", textAlign: "justify", lineHeight: 1.25 }}>
          Describe your app. Kadana builds it across web, iOS and Android, with local compliance-readiness
          and cultural compatibility built in by default, scaling to any market on Earth,
          powered by our patent-pending BeCo Index guiding every prompt.
        </h2>
      </section>



      {/* SW7 — resume banner */}
      {showResume && savedSel && (
        <section style={{ margin: "0 auto", padding: "0 24px" }}>
          <div className="k-container k-fadein" data-testid="resume-banner"
            style={{ padding: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap",
              borderLeft: "3px solid var(--accent-primary)" }}>
            <RotateCcw size={16} color="var(--accent-primary)" />
            <span className="k-heading" style={{ fontSize: 14, flex: "1 1 260px" }}>
              Resume where you left off? {savedSel.prompt
                ? `“${savedSel.prompt.slice(0, 70)}${savedSel.prompt.length > 70 ? "…" : ""}”`
                : "Your last sector and region picks?"}
            </span>
            <button className="k-btn k-btn-primary" data-testid="resume-restore" style={{ padding: "8px 16px", fontSize: 14 }} onClick={restoreDraft}>Restore</button>
            <button className="k-btn k-btn-ghost" data-testid="resume-dismiss" style={{ padding: "8px 16px", fontSize: 14 }} onClick={clearDraft}>Start fresh</button>
          </div>
        </section>
      )}

      {/* Hero prompt console */}
      <section style={{ margin: "0 auto", padding: "8px 24px 40px" }}>
        <div className="k-container" data-testid="prompt-console" style={{ padding: 20 }}>
          <PromptLangPicker />
          <textarea className="k-control" data-testid="hero-prompt" rows={4}
            aria-label="Describe your app idea"
            placeholder="Describe the app idea you want to build..."
            value={prompt} onChange={e => setPrompt(e.target.value)} />
          <SpellCheck text={prompt} onApply={setPrompt} apiPath="/public/lt/check" />
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
            <button className="k-btn k-btn-ghost" data-testid="copy-brief" style={{ padding: "6px 12px", fontSize: 13 }}
              onClick={copyBrief} disabled={!prompt.trim()}>
              <Copy size={13} /> Copy my build brief
            </button>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
            <select className="k-control" data-testid="picker-sector" style={{ width: "auto", flex: "1 1 220px" }}
              aria-label="Choose your app's sector"
              value={sector} onChange={e => setSector(e.target.value)}>
              <option value="">Sector…</option>
              {domains.map(d => <option key={d.domain} value={d.domain}>{d.label}</option>)}
            </select>
            <select className="k-control" data-testid="picker-jurisdiction" style={{ width: "auto", flex: "1 1 200px" }}
              aria-label="Choose your target market or region"
              value={jur} onChange={e => setJur(e.target.value)}>
              {regionGroups.map(g => (
                <optgroup key={g.label} label={g.label}>
                  {g.options.map(o => (
                    <option key={o.code + g.label} value={o.code}>{o.name}{o.zero ? " (no connectors yet)" : ""}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            <button className="k-btn k-btn-ghost" data-testid="picker-connectors" onClick={() => setShowConnectors(true)}>
              Browse connectors
            </button>
          </div>
          {sector && jur && reco?.ready && (
            <div data-testid="reco-preview" className="k-control" style={{ marginTop: 12, padding: 12 }}>
              <p className={reco.zero_coverage ? "k-tag-warn" : "k-tag-info"} style={{ fontSize: 13, margin: 0 }}>
                {reco.zero_coverage
                  ? reco.zero_coverage_message
                  : `${reco.connector_count} connectors and ${reco.layer_count} compliance layers likely apply for ${reco.domain_label}.`}
              </p>
              {reco.price_range && (
                <p className="k-muted" data-testid="reco-price-range"
                  style={{ fontSize: 13, marginTop: 6, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span>Indicative fixed-price range: {curFormat(reco.price_range.gbp_low, { decimals: 0 })}–{curFormat(reco.price_range.gbp_high, { decimals: 0 })}</span>
                  <CurrencySelect compact />
                </p>
              )}
              {reco.mock_screen && (
                <p className="k-muted" data-testid="reco-mock-screen" style={{ fontSize: 13, marginTop: 6 }}>
                  Likely first screen: {reco.mock_screen}
                </p>
              )}
              {reco.readiness && (
                <p className="k-muted" data-testid="reco-readiness" style={{ fontSize: 12, marginTop: 6 }}>
                  {reco.readiness.label}: Kadana coverage {reco.readiness.depth} for {countryName(reco.readiness.market)}.
                </p>
              )}
              {reco.build_time && (
                <p className="k-muted" data-testid="reco-build-time" style={{ fontSize: 12, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
                  <Clock size={12} /> Estimated build time: {reco.build_time.label}
                </p>
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginTop: 14, flexWrap: "wrap" }}>
            <button className="k-btn k-btn-primary" data-testid="start-building"
              style={{ fontSize: 16, padding: "13px 26px" }} onClick={startBuilding}>
              Start building <ArrowRight size={16} />
            </button>
            <label className="k-muted" style={{ fontSize: 13, display: "flex", gap: 6, alignItems: "center" }}>
              <input type="checkbox" data-testid="human-check" checked={humanCheck}
                onChange={e => setHumanCheck(e.target.checked)} /> I'm human
            </label>
            <button className="k-btn k-btn-ghost" data-testid="free-preview" onClick={tryPreview} disabled={!humanCheck}>
              <Sparkles size={14} /> Try one free clarifying question
            </button>
          </div>
          {preview && (
            <div className="k-control" data-testid="preview-result" style={{ marginTop: 14 }}>
              <div className="k-heading" style={{ fontWeight: 600, fontSize: 14 }}>A quick clarification (preview)</div>
              <ul className="k-muted" style={{ fontSize: 13, marginTop: 6 }}>
                {preview.clarifying_questions.map((q, i) => <li key={i}>{q}</li>)}
              </ul>
              <p className="k-muted" style={{ fontSize: 12 }}>{preview.note}</p>
              <button className="k-btn k-btn-primary" onClick={() => nav("/register", { state: { prompt, sector, jurisdiction: jur, price_range: reco?.price_range } })}>{preview.cta}</button>
            </div>
          )}
        </div>
      </section>

      <CompareMarkets />

      <PublicFooter patent={m?.patent} />

      {showConnectors && <ConnectorPreview onClose={() => setShowConnectors(false)} />}
    </div>
  );
}